<template>
    <form @submit.prevent="handleSubmit()" id="formQuotation">
        <div class="form-row mt-1">
            <div class="form-group">
                <label for="cost-input" class="font-semibold">Costo de la compra*</label>
                <div class="input-group">
                    <input
                        :class="['custom-input', {'is-invalid': errors.includes('cost')}]"
                        v-model="form.cost"
                        id="cost-input"
                        name="cost"
                        type="number"
                        placeholder="0.00 USD"
                        validate step="0.01"
                        @focus="fieldFocusEvent('cost')"
                    >
                    <span class="font-bold">USD</span>
                </div>
            </div>
            <div class="form-group">
                <label class="font-semibold">Código postal de destino*</label>
                <input
                    :class="['custom-input', {'is-invalid': errors.includes('destination')}]"
                    name="destination"
                    v-model="form.destination"
                    validate pattern="\d*"
                    oninput="this.value = this.value.replace(/\D/g, '')"
                    maxlength="5"
                    @focus="fieldFocusEvent('destination')"
                >
            </div>
        </div>
        <div>
            <h2 class="text-center font-bold mt-2">{{texts.subtitle}}</h2>
            <p class="text-center my-1">{{texts.subtitle_description}}</p>
            <div class="form-row mt-1" v-if="!form.chboxDimensions">
                <div class="form-group">
                    <label class="font-semibold">Largo</label>
                    <div class="input-group-reverse">
                        <input
                            type="number"
                            name="width"
                            v-model="form.width"
                            :class="['custom-input', {'is-invalid': errors.includes('width')}]"
                            @focus="fieldFocusEvent('width')"
                        >
                        <span class="font-bold">{{getMeasuresLabel()}}</span>
                    </div>
                </div>
                <div class="form-group">
                    <label class="font-semibold">Alto</label>
                    <div class="input-group-reverse">
                        <input
                            type="number"
                            name="height"
                            v-model="form.height"
                            :class="['custom-input', {'is-invalid': errors.includes('height')}]"
                            @focus="fieldFocusEvent('height')"
                        >
                        <span class="font-bold">{{getMeasuresLabel()}}</span>
                    </div>
                </div>
                <div class="form-group">
                    <label class="font-semibold">Profundidad</label>
                    <div class="input-group-reverse">
                        <input
                            type="number"
                            name="length"
                            v-model="form.length"
                            :class="['custom-input', {'is-invalid': errors.includes('length')}]"
                            @focus="fieldFocusEvent('length')"
                        >
                        <span class="font-bold">{{getMeasuresLabel()}}</span>
                    </div>
                </div>
                <div class="form-group">
                    <label class="font-semibold">Peso</label>
                    <div class="input-group-reverse">
                        <input
                            type="number"
                            name="weight"
                            v-model="form.weight"
                            :class="['custom-input', {'is-invalid': errors.includes('weight')}]"
                            @focus="fieldFocusEvent('weight')"
                        >
                        <span class="font-bold">{{getWeightLabel()}}</span>
                    </div>
                </div>
            </div>

            <div class="form-row mt-1" v-else>
                <div class="form-group">
                    <label class="font-semibold">Peso</label>
                    <div class="input-group-reverse">
                        <input
                            type="number"
                            name="weight"
                            v-model="form.weight"
                            :class="['custom-input', {'is-invalid': errors.includes('weight')}]"
                            @focus="fieldFocusEvent('weight')"
                        >
                        <span class="font-bold">{{getWeightLabel()}}</span>
                    </div>
                </div>
            </div>

            <div class="form-row mt-1" style="gap: .5rem;">
                <input
                    v-model="form.chboxDimensions"
                    id="chbox-dimensions"
                    name="chboxDimensions"
                    type="checkbox"
                    class="custom-checkbox"
                    @focus="fieldFocusEvent('chboxDimensions')"
                >
                <label for="chbox-dimensions" class="font-semibold font-sm">No conozco las medidas</label>
            </div>

            <div class="form-row mt-1" style="gap: .5rem;" v-if="quotImportation === 'true'">
                <input
                    :value="quotOnlyImportation"
                    @input="$emit('update:quotOnlyImportation', $event.target.value)"
                    id="chbox-importation"
                    name="chboxImportation"
                    type="checkbox"
                    class="custom-checkbox"
                    @focus="fieldFocusEvent('chboxImportation')"
                >
                <label for="chbox-importation" class="font-semibold font-sm">Orden sin envío</label>
            </div>

            <TransitionLabel
                :show="alert.show"
                class="text-h6 text-bold mt-1 danger-color"
            >
                ⚠️ {{ alert.message }}
            </TransitionLabel>

            <div class="w-100 row center">
                <UnitSelector
                    :modelValue="unitSystem"
                    @update:modelValue="value => $emit('update:unitSystem', value)"
                ></UnitSelector>
            </div>

            <div class="w-100 row between my-1">
                <div class="row center" style="gap: 0.5rem;">
                    <ItemBadge
                        style="min-width: 100px;"
                        span="Peso Vol."
                        :label="`${getVolumetricWeight}`"
                    ></ItemBadge>

                    <ItemBadge
                        style="min-width: 120px;"
                        span="Volumen"
                        :label="`${getImportationVolume} m³`"
                    ></ItemBadge>
                </div>

                <button :disabled="btnDisabled" type="submit" class="btn-primary mt-1">Cotizar mi envio</button>
            </div>

            <div v-if="btnDisabled" v-for="parcel in parcelsInProcess" :key="parcel" class="loader-container">
                <span>Cotizando en {{ utils.getParcelLabel(parcel) }}</span>
                <div class="loader-sm"></div>
            </div>
        </div>
    </form>
</template>

<script>
import ItemBadge from '../ItemBadge.vue'
import TransitionLabel from '../TransitionLabel.vue'
import UnitSelector from '../UnitSelector.vue'
import utils from '../../composables/utils'

export default {
    components: {
        ItemBadge,
        UnitSelector,
        TransitionLabel
    },
    props: {
        form: Object,
        alert: Object,
        unitSystem: String,
        parcelsInProcess: Array,
        btnDisabled: Boolean,
        quotOnlyImportation: Boolean,
        texts: {
            type: Object,
            default: () => ({
                subtitle: 'Peso y dimensiones',
                subtitle_description: ''
            })
        },
        quotImportation: {
            type: String,
            default: 'false'
        },
    },
    data () {
        return {
            errors: [],
            utils,
        }
    },
    methods: {
        fieldFocusEvent(inputVal) {
            this.$emit('trackFieldFocus', inputVal)
        },
         getVolumetricWeightByWheight (kg) {
            if (!kg) return 0
            let value = kg  ?? 0
            let volumetricDimension = Math.pow(value*5000,1/3)
            let dimension = volumetricDimension.toFixed(2)
            this.setDimensionsByVolumetricWheight(Number(dimension))
        },
        getVolumetricWeightByDimensions () {
            const length = this.getCmValue(this.form.length ?? 0)
            const width = this.getCmValue(this.form.width ?? 0)
            const height = this.getCmValue(this.form.height ?? 0)
            let volumetric = (length * width * height) / 5000
            return volumetric.toFixed(2)
        },
        getVolumeDimensions () {
            if (this.form.chboxDimensions) {
                const kilograms = this.getKilogramsValue(this.form.weight)
                const dimension = Math.pow(kilograms * 5000, 1/3)
                const value = Number.isFinite(dimension) ? dimension : 0
                return {
                    length: value,
                    width: value,
                    height: value,
                }
            }
            return {
                length: this.getCmValue(this.form.length ?? 0),
                width: this.getCmValue(this.form.width ?? 0),
                height: this.getCmValue(this.form.height ?? 0),
            }
        },
        getMeasuresLabel () {
            return this.unitSystem === 'metric' ? 'CM' : 'IN'
        },
        getWeightLabel () {
            return this.unitSystem === 'metric' ? 'KG' : 'LB'
        },
        setDimensionsByVolumetricWheight (dimension) {
            this.form.length = dimension
            this.form.width = dimension
            this.form.height = dimension
        },
        isvalidForm(){
            this.errors = [];
            if(!this.form.cost) this.errors.push('cost');
            if(!this.form.destination || this.form.destination.length !== 5) this.errors.push('destination');
            if(!this.form.chboxDimensions && !this.form.length) this.errors.push('length');
            if(!this.form.chboxDimensions && !this.form.width) this.errors.push('width');
            if(!this.form.chboxDimensions && !this.form.height) this.errors.push('height');
            if(!this.form.weight) this.errors.push('weight');

            return this.errors.length == 0;
        },
        getForm() {
            const rawForm = JSON.parse(JSON.stringify(this.form || {}));
            const cost = this.getNumberValue(this.form.cost);
            const length = this.roundUpDimension(this.getCmValue(this.form.length ?? 0));
            const weight = this.roundUpDimension(this.getKilogramsValue(this.form.weight));
            const width = this.roundUpDimension(this.getCmValue(this.form.width ?? 0));
            const height = this.roundUpDimension(this.getCmValue(this.form.height ?? 0));
            return {
                ...rawForm,
                unitSystem: this.unitSystem,
                cost,
                length,
                weight,
                width,
                height,
                originalValues: rawForm,
                originalUnitSystem: this.unitSystem,
            }
        },
        roundUpDimension(value) {
            if (value === null || value === undefined || value === '') return 0;
            const numeric = Number(value);
            if (Number.isNaN(numeric)) return 0;
            return Math.ceil(numeric);
        },
        getNumberValue (value) {
            if (value === null || value === undefined || value === '') return 0
            const numeric = Number(value)
            if (Number.isNaN(numeric)) return 0
            return Number(numeric.toFixed(2))
        },
        getKilogramsValue (pounds) {
            if (pounds === null || pounds === undefined || pounds === '') return 0
            let conversion = Number(pounds)
            if (Number.isNaN(conversion)) conversion = 0
            if (this.unitSystem === 'imperial') conversion = conversion * 0.45359237
            return Number(conversion.toFixed(2))
        },
        getCmValue (value) {
            if (value === null || value === undefined || value === '') return 0
            let conversion = Number(value)
            if (Number.isNaN(conversion)) conversion = 0
            if (this.unitSystem === 'imperial') conversion = conversion * 2.54
            return Number(conversion.toFixed(2))
        },
        handleSubmit() {
            this.$emit('update:alert', {show: false, message: ''})

            console.log(this.errors)
            if(this.isvalidForm()) {
                console.log(this.errors)
                this.$emit('submitForm', this.getForm());
            }
        },
    },
    computed: {
        getVolumetricWeight () {
            if (this.form.chboxDimensions) this.getVolumetricWeightByWheight(this.getKilogramsValue(this.form.weight))
            return this.getVolumetricWeightByDimensions()
        },
        getImportationVolume () {
            const { length, width, height } = this.getVolumeDimensions()
            const volume = (length * width * height) / 1000000
            return Number.isFinite(volume) ? volume.toFixed(3) : '0.000'
        },
    }
}
</script>
